import { useState } from 'react';
import type { NodeConnections } from '../../types/nodeConfig';
import {
  gateRoutesConfiguredSame,
  resolveGateRouteIds,
} from '../../lib/gateRoutes';
import SearchableRouteSelect from './SearchableRouteSelect';

interface Props {
  node: Record<string, unknown>;
  connections: NodeConnections;
  readOnly?: boolean;
  onChange: (patch: Record<string, unknown>) => void;
}

export default function GateNodeConfig({
  node,
  connections,
  readOnly,
  onChange,
}: Props) {
  const outgoing = connections.outgoing;
  const schedule =
    (node.schedule as Array<{ start_time_seconds: number; state: string }>) ??
    [];
  const [showSchedule, setShowSchedule] = useState(schedule.length > 0);

  const routeIndex = (routeId: string | undefined) => {
    if (!routeId) return 0;
    const idx = outgoing.findIndex((r) => r.route_id === routeId);
    return idx >= 0 ? idx : 0;
  };

  const { openRouteId, closedRouteId } = resolveGateRouteIds(
    node as import('../../types/project').ProjectNode,
    outgoing,
  );

  const setOpenRoute = (routeId: string) => {
    onChange({
      open_route_id: routeId,
      open_route_index: routeIndex(routeId),
    });
  };

  const setClosedRoute = (routeId: string) => {
    onChange({
      closed_route_id: routeId,
      closed_route_index: routeIndex(routeId),
    });
  };

  const sameRoute = gateRoutesConfiguredSame(openRouteId, closedRouteId);

  return (
    <section className="space-y-4 rounded-lg border border-border bg-surface/40 p-4">
      <h3 className="panel-section-title">Gate configuration</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-text-muted">Initial state</label>
          <select
            className="input-field w-full"
            disabled={readOnly}
            value={String(node.initial_state ?? 'open')}
            onChange={(e) => onChange({ initial_state: e.target.value })}
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">Gate group</label>
          <input
            type="text"
            className="input-field w-full"
            disabled={readOnly}
            value={String(node.group ?? '')}
            placeholder="e.g. line-a"
            onChange={(e) => onChange({ group: e.target.value })}
          />
          <p className="mt-1 text-[10px] text-text-subtle">
            Gates with the same group are controlled together in simulation.
          </p>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-text-muted">
        <input
          type="checkbox"
          disabled={readOnly}
          checked={node.manual_control_enabled !== false}
          onChange={(e) =>
            onChange({ manual_control_enabled: e.target.checked })
          }
        />
        Manual control during simulation
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-text-muted">When OPEN → route</label>
          <SearchableRouteSelect
            routes={outgoing}
            value={openRouteId ?? ''}
            direction="outgoing"
            disabled={readOnly || outgoing.length === 0}
            onChange={setOpenRoute}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">When CLOSED → route</label>
          <SearchableRouteSelect
            routes={outgoing}
            value={closedRouteId ?? ''}
            direction="outgoing"
            disabled={readOnly || outgoing.length === 0}
            onChange={setClosedRoute}
          />
        </div>
      </div>

      {sameRoute && (
        <p className="alert-warning px-3 py-2 text-xs">
          Open and closed states route to the same path — flow will not change when toggling.
        </p>
      )}

      <div>
        <label className="mb-2 flex items-center gap-2 text-xs font-medium text-text-muted">
          <input
            type="checkbox"
            disabled={readOnly}
            checked={showSchedule}
            onChange={(e) => {
              setShowSchedule(e.target.checked);
              if (!e.target.checked) onChange({ schedule: [] });
            }}
          />
          Use time-based schedule
        </label>

        {showSchedule && (
          <>
            {schedule.map((entry, index) => (
              <div key={index} className="mb-2 flex gap-2">
                <input
                  type="number"
                  className="input-field w-28 text-xs"
                  disabled={readOnly}
                  value={entry.start_time_seconds}
                  onChange={(e) => {
                    const next = [...schedule];
                    next[index] = {
                      ...entry,
                      start_time_seconds: Number(e.target.value),
                    };
                    onChange({ schedule: next });
                  }}
                  placeholder="Start sec"
                />
                <select
                  className="input-field flex-1 text-xs"
                  disabled={readOnly}
                  value={entry.state}
                  onChange={(e) => {
                    const next = [...schedule];
                    next[index] = { ...entry, state: e.target.value };
                    onChange({ schedule: next });
                  }}
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
                {!readOnly && (
                  <button
                    type="button"
                    className="btn-danger-ghost"
                    onClick={() =>
                      onChange({
                        schedule: schedule.filter((_, i) => i !== index),
                      })
                    }
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {!readOnly && (
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() =>
                  onChange({
                    schedule: [
                      ...schedule,
                      { start_time_seconds: 0, state: 'open' },
                    ],
                  })
                }
              >
                Add schedule entry
              </button>
            )}
          </>
        )}
      </div>

      {outgoing.length > 2 && (
        <p className="rounded-md border border-border-subtle bg-surface/60 px-3 py-2 text-xs text-text-subtle">
          This gate has {outgoing.length} outgoing routes. Open/closed states
          map to two routes; additional routes are not toggled by the gate.
        </p>
      )}

      {outgoing.length === 0 && (
        <p className="text-xs text-warning-on-dark">
          No outgoing routes connected — connect routes before configuring gate paths.
        </p>
      )}
    </section>
  );
}
